import { useTranslation } from 'react-i18next'

import {
  HStack,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaTriangleExclamationSolid,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'
import type { MismatchItem } from '~/pages/ProcurementPlanDetail/models/mismatchItems'

export type MismatchNavBannerProps = {
  items: readonly MismatchItem[]
  focusedIndex: number | undefined
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}

/**
 * 不整合バナー (AC-PPD-013〜017。design `plan/submission-detail.html` の `.sd-mismatch-nav`)。
 * 件数・注目中の箇所 (調達 / 販売・取引先名・時刻範囲)・前へ / N / M / 次へ・閉じるを並べる。
 * 件が無い時は描画しない。状態 (注目中の件・閉じたか) は呼び出し側の hook が持つ。
 */
export const MismatchNavBanner = ({
  items,
  focusedIndex,
  onPrev,
  onNext,
  onClose,
}: MismatchNavBannerProps): React.ReactElement | null => {
  const { t } = useTranslation()
  if (items.length === 0) return null

  const focused = focusedIndex === undefined ? undefined : items[focusedIndex]
  const closeLabel = t('page:procurementPlanDetail.mismatchNav.close')

  return (
    <HStack
      flexWrap="wrap"
      gap={2}
      borderWidth="1px"
      borderColor="Border.negativeMid"
      borderRadius="md"
      aria-label={t('page:procurementPlanDetail.mismatchNav.region')}
      backgroundColor="Surface.negativeLow"
      paddingX={3}
      paddingY={2}
      role="region"
    >
      <HStack gap={1} color="Text.negativeHigh" whiteSpace="nowrap">
        <FaTriangleExclamationSolid
          boxSize={iconSize.gridInline}
          aria-hidden="true"
        />
        <Text as="span" fontWeight="bold">
          {t('page:procurementPlanDetail.mismatchNav.count', {
            count: items.length,
          })}
        </Text>
      </HStack>
      <Text
        as="span"
        minWidth={0}
        color="Text.highEmphasis"
        fontWeight="medium"
      >
        {focused === undefined ? '' : formatDetail(focused, t)}
      </Text>
      <HStack flexShrink={0} gap={1.5} marginLeft="auto">
        <TertiaryButton onClick={onPrev} size="sm">
          {t('page:procurementPlanDetail.mismatchNav.prev')}
        </TertiaryButton>
        <Text
          as="span"
          minWidth="56px"
          color="Text.negativeMid"
          fontWeight="semibold"
          textAlign="center"
          fontVariantNumeric="tabular-nums"
        >
          {t('page:procurementPlanDetail.mismatchNav.position', {
            current:
              focusedIndex === undefined ? '—' : String(focusedIndex + 1),
            total: items.length,
          })}
        </Text>
        <TertiaryButton onClick={onNext} size="sm">
          {t('page:procurementPlanDetail.mismatchNav.next')}
        </TertiaryButton>
        <Tooltip content={closeLabel}>
          <TertiaryButton
            width="32px"
            padding={0}
            aria-label={closeLabel}
            onClick={onClose}
            size="sm"
          >
            <FaXmarkRegular boxSize={iconSize.gridInline} aria-hidden="true" />
          </TertiaryButton>
        </Tooltip>
      </HStack>
    </HStack>
  )
}

/** 「{調達|販売} / {取引先名} / {HH:MM}〜{HH:MM} ({n}コマ)」 (design の書式に conventions.md のかっこ前の半角スペースを足したもの)。範囲なしの件は時刻を出さない。 */
function formatDetail(
  item: MismatchItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const side = t(
    item.side === 'procurement'
      ? 'page:procurementPlanDetail.mismatchNav.sideProcurement'
      : 'page:procurementPlanDetail.mismatchNav.sideSales',
  )
  if (item.range === undefined) {
    return t('page:procurementPlanDetail.mismatchNav.detailWithoutRange', {
      side,
      partner: item.counterpartyName,
    })
  }
  const { fromIndex, toIndexExclusive } = item.range
  return t('page:procurementPlanDetail.mismatchNav.detail', {
    side,
    partner: item.counterpartyName,
    from: komaNumberToTime(fromIndex + 1),
    to: komaEndTime(komaNumberToTime(toIndexExclusive)),
    count: toIndexExclusive - fromIndex,
  })
}
